/**
 * Sprint 16 · S16.3 · BottomNav role-aware (Mockup-Punkt 30).
 *
 * Sichtbar < 768px UND role !== super_admin (Super-Admin = Desktop-only).
 * - employee  (3): Erfassen · Meine Leads · Profil
 * - dispatcher (4): Erfassen · Alle Leads · Versand · Profil
 * - operator_admin (5): Dashboard · Leads · Team · Settings · Profil
 *
 * Lucide-Icons exakt wie Mockup §30 (mobile-nav).
 */
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  List,
  PlusCircle,
  Send,
  User as UserIcon,
  Users,
} from 'lucide-react';
import { useAuth } from '../lib/auth';

interface Tab {
  to: string;
  label: string;
  Icon: typeof PlusCircle;
}

const TABS_BY_ROLE: Record<string, Tab[]> = {
  employee: [
    { to: '/capture', label: 'Erfassen', Icon: PlusCircle },
    { to: '/leads', label: 'Meine Leads', Icon: List },
    { to: '/profile', label: 'Profil', Icon: UserIcon },
  ],
  dispatcher: [
    { to: '/capture', label: 'Erfassen', Icon: PlusCircle },
    { to: '/leads', label: 'Alle Leads', Icon: List },
    { to: '/leads?versand=1', label: 'Versand', Icon: Send },
    { to: '/profile', label: 'Profil', Icon: UserIcon },
  ],
  operator_admin: [
    // Sprint 42 · Branding raus aus Mobile-BottomNav (auf Handy nicht relevant
    // — operator_admin macht Branding ohnehin am Desktop). 4 Tabs reichen.
    { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { to: '/leads', label: 'Leads', Icon: List },
    { to: '/users', label: 'Team', Icon: Users },
    { to: '/profile', label: 'Profil', Icon: UserIcon },
  ],
};

export function BottomNav() {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return null;
  // Super-Admin: keine BottomNav (Desktop-only)
  if (user.role === 'super_admin') return null;

  const tabs = TABS_BY_ROLE[user.role] ?? TABS_BY_ROLE.employee;

  return (
    <nav className="bottom-nav" aria-label="Hauptnavigation">
      {tabs.map(({ to, label, Icon }) => {
        const [path, query] = to.split('?');
        const queryActive =
          !query ||
          (query &&
            location.search.includes(query.split('=')[0] + '=' + query.split('=')[1]));
        const active =
          (location.pathname === path ||
            (path !== '/dashboard' && path !== '/' && location.pathname.startsWith(path))) &&
          queryActive;
        return (
          <Link
            key={to}
            to={to}
            className={`bottom-nav__tab ${active ? 'bottom-nav__tab--active' : ''}`}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
          >
            <Icon size={22} strokeWidth={2} aria-hidden="true" />
            <span className="bottom-nav__label">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
